// pages/Sponsors.jsx - Fully Responsive
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { RiArrowRightLine, RiDownloadLine, RiFilePdfLine } from "react-icons/ri";
import { API_BASE } from "../config";
import { useSummit } from "../pages/summitdetails";
import toast from "react-hot-toast";

/* ─── THEME COLORS ─────────────────────────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
  gradAccent: "linear-gradient(135deg, #3BBCAE 0%, #2A9D8F 100%)",
  gradSuccess: "linear-gradient(135deg, #2A9D8F 0%, #6BC2B5 100%)",
  success: "#2A9D8F",
  warning: "#D4A017",
  danger: "#218577",
};

/* ─── CSS ──────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth;-webkit-font-smoothing:antialiased}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  ::-webkit-scrollbar{width:6px}
  ::-webkit-scrollbar-track{background:${T.borderLight}}
  ::-webkit-scrollbar-thumb{background:${T.primaryLight};border-radius:4px}

  .t-hero{background:${T.gradSoft};border-bottom:1px solid ${T.border};padding:clamp(32px,6vw,80px) clamp(16px,4vw,64px) clamp(32px,5vw,64px);position:relative;overflow:hidden}
  .t-hero::before{content:'';position:absolute;top:-120px;left:50%;transform:translateX(-50%);width:600px;height:600px;background:radial-gradient(circle,rgba(42,157,143,0.08) 0%,transparent 70%);pointer-events:none}
  .t-hero-i{max-width:1320px;margin:0 auto;display:flex;align-items:flex-start;justify-content:space-between;gap:24px;flex-wrap:wrap;position:relative}
  .t-hero-lbl{display:inline-flex;align-items:center;gap:6px;padding:4px 12px;border-radius:20px;background:${T.softTeal};border:1px solid ${T.border};color:${T.primary};font-size:clamp(10px,0.8vw,11px);font-weight:700;letter-spacing:.08em;text-transform:uppercase;margin-bottom:clamp(12px,2vw,20px)}
  .t-hero-h{font-family:'Playfair Display',serif;font-size:clamp(1.6rem,4.5vw,3.4rem);font-weight:900;color:${T.navy};letter-spacing:-.03em;line-height:1.06;margin-bottom:clamp(10px,1.5vw,14px)}
  .t-hero-h em{font-style:normal;color:${T.accentGold}}
  .t-hero-p{font-size:clamp(0.85rem,1.3vw,1rem);color:${T.textMid};line-height:1.7;max-width:500px}
  .t-stats{display:flex;gap:1px;background:${T.border};border-radius:12px;border:1px solid ${T.border};overflow:hidden;align-self:flex-start;flex-shrink:0;flex-wrap:wrap}
  .t-st{background:${T.white};padding:clamp(14px,2vw,20px) clamp(18px,3vw,28px);display:flex;flex-direction:column;gap:4px;min-width:70px;flex:1}
  .t-st:nth-child(2){background:${T.offWhite}}
  .t-st-n{font-family:'Playfair Display',serif;font-size:clamp(1.4rem,2.2vw,1.8rem);font-weight:900;color:${T.accentGold};line-height:1;letter-spacing:-.03em}
  .t-st-l{font-size:clamp(9px,0.7vw,11px);color:${T.textMuted};font-weight:500;text-transform:uppercase;letter-spacing:.06em}

  .t-card{background:${T.white};border-radius:16px;border:1px solid ${T.borderLight};padding:clamp(16px,2.5vw,28px);box-shadow:${T.shadow};transition:all 0.3s ease}
  .t-card:hover{transform:translateY(-4px);box-shadow:${T.shadowLg}}

  .t-cta-w{padding:0 clamp(12px,4vw,64px) clamp(32px,5vw,48px);background:${T.offWhite}}
  .t-cta{max-width:1320px;margin:0 auto;background:${T.gradNavy};border:1px solid rgba(59,188,174,0.28);border-radius:16px;padding:clamp(20px,4vw,52px) clamp(16px,4vw,60px);display:flex;align-items:center;justify-content:space-between;gap:24px;flex-wrap:wrap;position:relative;overflow:hidden}
  .t-cta::before{content:'';position:absolute;inset:0;background:radial-gradient(circle at 50% 100%,rgba(212,160,23,0.12) 0%,transparent 60%);pointer-events:none}
  .t-cta-tag{display:inline-block;padding:3px 10px;border-radius:20px;background:rgba(212,160,23,0.15);border:1px solid rgba(212,160,23,0.25);color:${T.accentGoldLight};font-size:clamp(10px,0.8vw,11px);letter-spacing:.06em;text-transform:uppercase;margin-bottom:8px;font-weight:700}
  .t-cta-h{font-family:'Playfair Display',serif;font-size:clamp(1.2rem,2.5vw,2rem);font-weight:900;color:${T.white};letter-spacing:-.025em;line-height:1.15}
  .t-cta-p{font-size:clamp(13px,1.2vw,14px);color:${T.textMuted};margin-top:6px;line-height:1.6}
  .t-cta-buttons{display:flex;gap:clamp(8px,1.5vw,16px);align-items:center;flex-wrap:wrap;width:100%}
  .t-cta-btn{display:inline-flex;align-items:center;gap:6px;padding:clamp(10px,1.5vw,12px) clamp(18px,2.5vw,28px);background:${T.gradGold};color:${T.navyDeep};border-radius:50px;text-decoration:none;font-family:'Inter',sans-serif;font-size:clamp(13px,1.1vw,14px);font-weight:700;transition:all .2s;justify-content:center;flex:1 1 auto;min-width:120px}
  .t-cta-btn:hover{background:${T.gradGoldReverse};transform:translateY(-2px);box-shadow:${T.shadowGold}}
  .t-cta-btn-outline{display:inline-flex;align-items:center;gap:6px;padding:clamp(10px,1.5vw,12px) clamp(18px,2.5vw,28px);background:transparent;border:1px solid rgba(255,255,255,0.3);color:${T.white};border-radius:50px;text-decoration:none;font-family:'Inter',sans-serif;font-size:clamp(13px,1.1vw,14px);font-weight:700;transition:all .2s;justify-content:center;flex:1 1 auto;min-width:120px}
  .t-cta-btn-outline:hover{background:rgba(255,255,255,0.1);transform:translateY(-2px)}

  .t-sponsor-grid{display:grid;gap:clamp(16px,2vw,24px)}
  .t-benefits-grid{display:grid;gap:clamp(10px,1.5vw,16px)}
  .t-packages-grid{display:grid;gap:clamp(20px,2.5vw,28px)}
  .t-section-title{font-family:'Playfair Display',serif;font-size:clamp(24px,3vw,32px);color:${T.navy};border-bottom:3px solid ${T.primary};padding-bottom:10px;margin-bottom:clamp(24px,3.5vw,36px);font-weight:700;letter-spacing:-0.01em}
  .t-sub-section-title{font-size:clamp(18px,2vw,22px);color:${T.text};margin-bottom:clamp(14px,2vw,20px);font-weight:600;display:flex;align-items:center;gap:10px}
  .t-sponsor-type-dot{display:inline-block;width:clamp(10px,1vw,12px);height:clamp(10px,1vw,12px);border-radius:50%;border:1px solid #e2e8f0;flex-shrink:0}
  .t-sponsor-card{text-align:center;padding:clamp(16px,2vw,24px);border:1px solid ${T.borderLight};border-radius:16px;background:${T.white};transition:all 0.3s ease;box-shadow:${T.shadow}}
  .t-sponsor-card:hover{transform:translateY(-4px);box-shadow:${T.shadowLg}}
  .t-sponsor-logo{max-width:100%;max-height:clamp(60px,8vw,80px);object-fit:contain;margin-bottom:10px}
  .t-sponsor-name{font-size:clamp(15px,1.3vw,16px);font-weight:700;color:${T.text};margin-bottom:4px}
  .t-sponsor-link{font-size:clamp(12px,1vw,13px);color:${T.primary};text-decoration:none;display:inline-block;margin-top:6px}
  .t-sponsor-link:hover{text-decoration:underline}
  .t-benefit-item{display:flex;align-items:center;gap:10px;padding:clamp(10px,1.2vw,12px) clamp(12px,1.5vw,16px);background:${T.offWhite};border-radius:10px;border:1px solid ${T.borderLight};transition:all 0.2s}
  .t-benefit-item:hover{border-color:${T.primary};box-shadow:${T.shadow}}
  .t-benefit-number{width:clamp(24px,2.5vw,28px);height:clamp(24px,2.5vw,28px);background:${T.gradPrimary};border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:clamp(12px,1.1vw,14px);flex-shrink:0}
  .t-benefit-text{font-size:clamp(13px,1.2vw,15px);font-weight:500;color:${T.text}}
  .t-package-card{padding:clamp(20px,2.5vw,28px);border:1px solid ${T.borderLight};border-radius:16px;background:${T.white};transition:all 0.3s ease;box-shadow:${T.shadow}}
  .t-package-card:hover{transform:translateY(-4px);box-shadow:${T.shadowLg};border-color:${T.primary}}
  .t-package-icon{font-size:clamp(32px,4vw,40px);margin-bottom:12px}
  .t-package-title{color:${T.text};margin-bottom:8px;font-size:clamp(17px,1.6vw,20px);font-weight:700}
  .t-package-price{font-size:clamp(22px,2.8vw,28px);font-weight:800;color:${T.primary};margin-bottom:16px}
  .t-package-price-sub{font-size:clamp(12px,1vw,13px);color:${T.textMuted};margin-bottom:16px}
  .t-package-list{padding-left:16px;font-size:clamp(13px,1.1vw,14px);color:${T.textMuted};line-height:1.8}
  .t-package-list li{margin-bottom:4px}
  .t-exhibition-box{background:linear-gradient(135deg,${T.softTeal} 0%,${T.white} 100%);padding:clamp(20px,3vw,32px) clamp(20px,3vw,36px);border-radius:16px;margin-bottom:clamp(32px,4vw,60px);border:1px solid ${T.border}}
  .t-exhibition-title{font-size:clamp(20px,2.2vw,24px);color:${T.navy};font-weight:700;margin:0;font-family:'Playfair Display',serif;display:flex;align-items:center;gap:10px}
  .t-exhibition-time{font-size:clamp(14px,1.3vw,16px);color:${T.textMid};margin-bottom:6px}
  .t-exhibition-note{font-size:clamp(12px,1vw,14px);color:${T.textMuted};margin-top:10px}
  .t-brochure-btn{display:inline-flex;align-items:center;gap:8px;padding:clamp(12px,1.8vw,16px) clamp(24px,3.5vw,40px);background:${T.gradGold};color:${T.navyDeep};font-weight:700;border-radius:40px;border:none;font-size:clamp(14px,1.2vw,16px);cursor:pointer;transition:all 0.3s ease;box-shadow:${T.shadowGold};width:100%;justify-content:center;max-width:400px}
  .t-brochure-btn:hover{transform:translateY(-2px);box-shadow:${T.shadowLg}}
  .t-brochure-placeholder{display:inline-flex;align-items:center;gap:10px;padding:clamp(12px,1.5vw,16px) clamp(20px,2.5vw,32px);background:${T.offWhite};border:2px dashed ${T.border};border-radius:40px;color:${T.textMuted};font-size:clamp(13px,1.1vw,14px);width:100%;justify-content:center;max-width:400px}

  @keyframes spin{to{transform:rotate(360deg)}}

  /* Responsive Overrides */
  @media(max-width:768px){
    .t-hero-i{flex-direction:column;align-items:stretch}
    .t-stats{width:100%}
    .t-cta{flex-direction:column;align-items:stretch}
    .t-cta-buttons{flex-direction:column;width:100%}
    .t-cta-btn,.t-cta-btn-outline{width:100%;justify-content:center}
    .t-sponsor-grid{grid-template-columns:repeat(auto-fit,minmax(140px,1fr))}
    .t-packages-grid{grid-template-columns:1fr}
  }

  @media(max-width:480px){
    .t-hero{padding:20px 12px 24px}
    .t-hero-h{font-size:1.4rem}
    .t-hero-p{font-size:0.85rem}
    .t-stats{flex-direction:column}
    .t-st{padding:10px 14px;min-width:auto}
    .t-st-n{font-size:1.2rem}
    .t-sponsor-grid{grid-template-columns:1fr 1fr}
    .t-benefits-grid{grid-template-columns:1fr}
    .t-section-title{font-size:20px}
    .t-package-card{padding:16px}
    .t-package-price{font-size:22px}
    .t-exhibition-box{padding:16px}
    .t-exhibition-title{font-size:18px}
  }

  @media(max-width:360px){
    .t-hero-h{font-size:1.2rem}
    .t-sponsor-grid{grid-template-columns:1fr}
    .t-sponsor-card{padding:12px}
    .t-sponsor-logo{max-height:50px}
    .t-package-title{font-size:16px}
    .t-package-price{font-size:20px}
    .t-benefit-text{font-size:13px}
  }
`;

const Sponsors = () => {
  const { eventId } = useSummit();
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brochureInfo, setBrochureInfo] = useState(null);
  const [brochurePdfUrl, setBrochurePdfUrl] = useState("");
  const [loadingBrochure, setLoadingBrochure] = useState(false);

  useEffect(() => {
    if (eventId) {
      fetchSponsors();
      fetchBrochureInfo();
    } else {
      setLoading(false);
    }
  }, [eventId]);

  const fetchSponsors = async () => {
    try {
      setLoading(true);
      const url = `${API_BASE}/api/sponsors/event/${eventId}`;
      console.log(`📡 Fetching sponsors from: ${url}`);
      
      const { data } = await axios.get(url);
      console.log("📦 Sponsors data:", data);
      
      if (data.success) {
        setSponsors(data.sponsors || []);
      }
    } catch (error) {
      console.error("Error fetching sponsors:", error);
      setSponsors([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBrochureInfo = async () => {
    if (!eventId) return;
    try {
      setLoadingBrochure(true);
      const response = await axios.get(`${API_BASE}/api/brochure/info/${eventId}`);
      if (response.data.success) {
        setBrochureInfo(response.data);
        setBrochurePdfUrl(response.data.pdfUrl || "");
      } else {
        setBrochureInfo(null);
        setBrochurePdfUrl("");
      }
    } catch (error) {
      console.log("No brochure info found (this is normal if none uploaded)");
      setBrochureInfo(null);
      setBrochurePdfUrl("");
    } finally {
      setLoadingBrochure(false);
    }
  };

  const downloadBrochure = async () => {
    if (!eventId) {
      toast.error("No event specified");
      return;
    }
    try {
      const response = await axios.get(`${API_BASE}/api/brochure/download/${eventId}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Sponsorship-Brochure.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Brochure downloaded successfully!");
    } catch (error) {
      console.error("Error downloading brochure:", error);
      toast.error("Failed to download brochure. Please try again later.");
    }
  };

  const getSponsorTypeColor = (type) => {
    const colors = {
      platinum: "#E5E4E2",
      gold: "#FFD700",
      silver: "#C0C0C0",
      bronze: "#CD7F32",
      partner: "#1A56DB"
    };
    return colors[type] || "#6B8090";
  };

  const getSponsorTypeLabel = (type) => {
    const labels = {
      platinum: "Platinum Sponsor",
      gold: "Gold Sponsor",
      silver: "Silver Sponsor",
      bronze: "Bronze Sponsor",
      partner: "Partner"
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        minHeight: "400px" 
      }}>
        <div style={{ 
          width: "clamp(36px,4vw,48px)", 
          height: "clamp(36px,4vw,48px)", 
          border: "3px solid #E2F2F0", 
          borderTopColor: "#2A9D8F", 
          borderRadius: "50%", 
          animation: "spin 1s linear infinite" 
        }} />
      </div>
    );
  }

  const groupedSponsors = sponsors.reduce((acc, sponsor) => {
    const type = sponsor.sponsorType || 'partner';
    if (!acc[type]) acc[type] = [];
    acc[type].push(sponsor);
    return acc;
  }, {});

  const sponsorTypeOrder = ['platinum', 'gold', 'silver', 'bronze', 'partner'];

  return (
    <div style={{ background: T.offWhite, minHeight: "100vh" }}>
      <style>{css}</style>

      {/* ══════════ HERO SECTION ══════════ */}
      <section className="t-hero">
        <div className="t-hero-i">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="t-hero-lbl">
              <span style={{ fontSize: "clamp(14px,1.5vw,16px)" }}>🏆</span>
              Meetings21 · Sponsorship
            </div>
            <h1 className="t-hero-h">
              Sponsorship <em>Opportunities</em>
            </h1>
            <p className="t-hero-p">
              Partner with us to reach a global audience of researchers, scientists, and industry leaders.
              Showcase your brand and connect with the immunology community.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18 }}>
              <div style={{ flex: 1, height: 2, maxWidth: 60, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 6, height: 6, background: T.accentGold, transform: 'rotate(45deg)' }} />
              <div style={{ flex: 1, height: 2, maxWidth: 60, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
          <div className="t-stats">
            <div className="t-st">
              <span className="t-st-n">{sponsors.length}</span>
              <span className="t-st-l">Sponsors</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">5</span>
              <span className="t-st-l">Tiers</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">50+</span>
              <span className="t-st-l">Benefits</span>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ MAIN CONTENT ══════════ */}
      <div style={{ maxWidth: 1320, margin: '0 auto', padding: 'clamp(24px,4vw,40px) clamp(12px,4vw,64px) ' + 'clamp(40px,6vw,80px)' }}>

        {/* Display sponsors by type */}
        {sponsors.length > 0 && (
          <section style={{ marginBottom: "clamp(40px,5vw,60px)" }}>
            <h2 className="t-section-title">
              Our Sponsors
            </h2>

            {sponsorTypeOrder.map(type => {
              const typeSponsors = groupedSponsors[type] || [];
              if (typeSponsors.length === 0) return null;

              return (
                <div key={type} style={{ marginBottom: "clamp(28px,3.5vw,40px)" }}>
                  <h3 className="t-sub-section-title">
                    <span className="t-sponsor-type-dot" style={{ background: getSponsorTypeColor(type) }} />
                    {getSponsorTypeLabel(type)}
                  </h3>
                  <div className="t-sponsor-grid" style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(clamp(140px,18vw,200px), 1fr))",
                    gap: "clamp(14px,2vw,24px)",
                  }}>
                    {typeSponsors.map((sponsor) => (
                      <div
                        key={sponsor._id}
                        className="t-sponsor-card"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = getSponsorTypeColor(type);
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = T.borderLight;
                        }}
                      >
                        {sponsor.logoUrl ? (
                          <img
                            src={sponsor.logoUrl}
                            alt={sponsor.name}
                            className="t-sponsor-logo"
                          />
                        ) : (
                          <div style={{ fontSize: "clamp(32px,4vw,40px)", marginBottom: 10 }}>🏢</div>
                        )}
                        <h4 className="t-sponsor-name">
                          {sponsor.name}
                        </h4>
                        {sponsor.url && (
                          <a
                            href={sponsor.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="t-sponsor-link"
                          >
                            Visit Website →
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </section>
        )}

        {/* Why Sponsor Section */}
        <section style={{ marginBottom: "clamp(40px,5vw,70px)" }}>
          <h2 className="t-section-title">
            Why You Should Join as Sponsor / Exhibitor?
          </h2>

          <p style={{
            fontSize: "clamp(15px,1.3vw,17px)",
            marginBottom: "clamp(14px,1.8vw,20px)",
            color: T.textMid,
            lineHeight: "1.7",
          }}>
            We are pleased to invite your participation as Sponsor or Exhibitor in{" "}
            <strong style={{ color: T.primary }}>Meetings21</strong>.
          </p>

          <p style={{
            fontSize: "clamp(14px,1.2vw,16px)",
            color: T.textMuted,
            lineHeight: "1.7",
            marginBottom: "clamp(14px,1.8vw,20px)",
          }}>
            The extensive 3-day program will bring together the global community
            of researchers, scientists, academicians, industry leaders, and
            government representatives to share knowledge and build valuable
            professional networks.
          </p>

          <p style={{
            fontSize: "clamp(14px,1.2vw,16px)",
            color: T.textMuted,
            lineHeight: "1.7",
            marginBottom: "clamp(24px,3vw,32px)",
          }}>
            Congress Partners are offered the valuable opportunity to associate
            their brand and profile with this significant event, demonstrating
            their commitment, innovation, and leadership in the field.
          </p>

          <h3 className="t-sub-section-title" style={{ marginBottom: "clamp(14px,2vw,20px)" }}>
            6 Key Benefits of Sponsoring:
          </h3>
          <div className="t-benefits-grid" style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(clamp(240px,30vw,280px), 1fr))",
            gap: "clamp(10px,1.5vw,16px)",
          }}>
            {[
              "Position your product as a market leader",
              "Maximum brand exposure to 1,000+ attendees",
              "Global Networking with industry pioneers",
              "High-quality Lead Generation opportunities",
              "Connect with the global immunology community",
              "Valuable Business Insights & market trends",
            ].map((benefit, idx) => (
              <div key={idx} className="t-benefit-item">
                <div className="t-benefit-number">{idx + 1}</div>
                <span className="t-benefit-text">{benefit}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ─── BROCHURE DOWNLOAD SECTION ─── */}
        <div style={{ textAlign: "center", margin: "clamp(32px,4vw,48px) 0" }}>
          {brochurePdfUrl ? (
            <button onClick={downloadBrochure} className="t-brochure-btn">
              <RiDownloadLine size="clamp(16px,1.5vw,20px)" /> Download Sponsorship Brochure
            </button>
          ) : (
            <div className="t-brochure-placeholder">
              <RiFilePdfLine size="clamp(20px,2vw,24px)" style={{ color: T.primary }} />
              <span>Brochure coming soon</span>
            </div>
          )}
        </div>

        {/* Exhibition Info */}
        <div className="t-exhibition-box">
          <div className="t-exhibition-title">
            <span style={{ fontSize: "clamp(24px,3vw,28px)" }}>🏛️</span>
            Exhibition
          </div>
          <p className="t-exhibition-time">
            <strong style={{ color: T.primary }}>Exhibition Time:</strong>{" "}
            09:00 - 17:00 on all conference days
          </p>
          <p className="t-exhibition-note">
            * Setup available from 08:00 on the first day | Dismantling after 17:00 on the final day
          </p>
        </div>

        {/* Other Sponsorship Packages */}
        <section>
          <h2 className="t-section-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: "clamp(24px,3vw,32px)" }}>🎯</span>
            Other Sponsorship Packages
          </h2>

          <div className="t-packages-grid" style={{
            display: "grid",
            gap: "clamp(20px,2.5vw,28px)",
            gridTemplateColumns: "repeat(auto-fit, minmax(clamp(280px,35vw,340px), 1fr))",
          }}>
            {/* Conference Bag */}
            <div className="t-package-card">
              <div className="t-package-icon">🎒</div>
              <h3 className="t-package-title">Conference Bag</h3>
              <div className="t-package-price">$2,000</div>
              <ul className="t-package-list">
                <li>Sponsor's logo on conference bags distributed to all attendees</li>
                <li>Logo placement on event website and app</li>
                <li>Standard sponsorship benefits package</li>
              </ul>
            </div>

            {/* Notebook / Pen / USB */}
            <div className="t-package-card">
              <div className="t-package-icon">✍️</div>
              <h3 className="t-package-title">Meeting Notebook / Pen / USB</h3>
              <div className="t-package-price">$2,000</div>
              <ul className="t-package-list">
                <li>Sponsor's branding on meeting notebooks / pens / USB drives</li>
                <li>Logo placement on event website</li>
                <li>Standard sponsorship benefits package</li>
              </ul>
            </div>

            {/* Best Poster */}
            <div className="t-package-card">
              <div className="t-package-icon">🏆</div>
              <h3 className="t-package-title">Best Poster Presentation</h3>
              <div className="t-package-price">$900</div>
              <div className="t-package-price-sub">($300 × 3 winners)</div>
              <ul className="t-package-list">
                <li>Sponsor's logo on Conference website as award sponsor</li>
                <li>Sponsor's branding on winner certificates</li>
                <li>Recognition during award ceremony</li>
              </ul>
            </div>

            {/* Best Oral */}
            <div className="t-package-card">
              <div className="t-package-icon">🎙️</div>
              <h3 className="t-package-title">Best Oral Presentation</h3>
              <div className="t-package-price">$1,000</div>
              <div className="t-package-price-sub">($500 × 2 winners)</div>
              <ul className="t-package-list">
                <li>Sponsor's logo on Conference website as award sponsor</li>
                <li>Sponsor's branding on winner certificates</li>
                <li>Recognition during award ceremony</li>
              </ul>
            </div>
          </div>
        </section>
      </div>

      {/* ── CTA SECTION ── */}
      <div className="t-cta-w">
        <motion.div 
          className="t-cta" 
          initial={{ opacity: 0, y: 18 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.4 }} 
          viewport={{ once: true }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="t-cta-tag">Interested in Sponsoring?</div>
            <h2 className="t-cta-h">Let's <span style={{ color: T.accentGold }}>Partner</span> Together</h2>
            <p className="t-cta-p">Contact us today to discuss customized sponsorship packages tailored to your goals.</p>
          </div>
          <div className="t-cta-buttons">
            <Link 
              to={eventId ? `/contact?eventId=${eventId}` : "/contact"}  
              className="t-cta-btn"
            >
              Contact Sponsorship Team <RiArrowRightLine size="clamp(13px,1.2vw,15px)" />
            </Link>
            <Link 
              to={eventId ? `/about?eventId=${eventId}` : "/about"}  
              className="t-cta-btn-outline"
            >
              Learn About Us <RiArrowRightLine size="clamp(13px,1.2vw,15px)" />
            </Link>
          </div>
        </motion.div>
      </div>

    </div>
  );
};

export default Sponsors;